/**
 * Scan POS Fast Wireless Barcode Path R1 -- the Sale screen as the target of
 * a wireless barcode gun (the phone).
 *
 *   BARCODE_SCAN (WebSocket) -> frame ok? -> repeat request_id? (same ACK, never twice)
 *     -> READY on the current channel? -> local-first lookup (R1 resolver, via the page)
 *     -> still READY on the SAME channel? -> shared Cart +1 -> BARCODE_ACK (WebSocket)
 *
 * Results (the phone beeps on these, nothing else):
 *   OK         the Cart really changed (+1, committed)
 *   NOT_FOUND  the product lookup proved there is no such product; Cart untouched
 *   NOT_READY  the Cart is provably untouched: page WAITING / PAUSED / LOCKED, the
 *              channel moved, the lookup could not be done, or the change was
 *              refused before it was made
 *   CHECK      the Cart MAY have changed and that cannot be proven -- the cashier
 *              looks at the screen before scanning again. Never retried.
 *
 * Separate from the P5 ScanEventReceiver on purpose: no scan_event_id, no HTTP
 * SCAN_ACK, no SCAN_STATUS. The only memory is a bounded per-page request_id
 * cache (memory only; a reload forgets it, which the fast path accepts).
 * Frames run one at a time in arrival order. Nothing here logs a barcode or
 * a request_id; timing reports carry numbers and categories only.
 */
import { ReceiverState } from '../api/scan-pos';
import { Binding, LookupOutcome, MutateOutcome, RejectReason } from './receiver';

export type FastResult = 'OK' | 'NOT_FOUND' | 'NOT_READY' | 'CHECK';

export interface BarcodeScanFrame {
    type: 'BARCODE_SCAN';
    request_id: string;
    barcode: string;
}

export interface FastTiming {
    path: 'FAST';
    outcome: FastResult | 'DUPLICATE';
    source?: 'LOCAL' | 'API';
    queuedMs: number;          // frame received -> its turn
    lookupMs?: number;
    cartMs?: number;           // frame received -> Cart changed (OK only)
    ackMs?: number;            // decision -> ACK handed to the socket
    totalMs: number;           // frame received -> ACK handed to the socket
    ackSent: boolean;
}

export interface FastReceiverDeps {
    currentBinding(): Binding | null;
    receiverState(): ReceiverState;
    lookup(barcode: string): Promise<LookupOutcome>;
    mutate(product: any): MutateOutcome;
    /** One send on the CURRENT verified socket of [binding]; false if it could not be. Never retried. */
    sendAck(binding: Binding, requestId: string, result: FastResult): boolean;
    onApplied?(product: any): void;
    /** Why the Cart was left untouched (reuses the page's existing messages). */
    onRejected?(reason: RejectReason): void;
    onCheck?(): void;
    onTiming?(timing: FastTiming): void;
    now?(): number;
}

const REQUEST_ID = /^[A-Za-z0-9_-]{16,64}$/;
export const FAST_CACHE_SIZE = 256;

const STATE_REASON: Record<Exclude<ReceiverState, 'READY'>, RejectReason> = {
    WAITING: 'SALE_NOT_READY',
    PAUSED: 'SALE_PAUSED',
    LOCKED: 'SALE_LOCKED',
};

export const parseBarcodeScanFrame = (frame: any): BarcodeScanFrame | null => {
    if (!frame || typeof frame !== 'object' || frame.type !== 'BARCODE_SCAN') return null;
    if (typeof frame.request_id !== 'string' || !REQUEST_ID.test(frame.request_id)) return null;
    if (typeof frame.barcode !== 'string' || frame.barcode.length < 1 || frame.barcode.length > 128) return null;
    return { type: 'BARCODE_SCAN', request_id: frame.request_id, barcode: frame.barcode };
};

const sameBinding = (a: Binding | null, b: Binding) =>
    !!a && a.channel === b.channel && a.surfaceId === b.surfaceId && a.generation === b.generation;

interface Marks {
    receivedAt: number;
    startedAt?: number;
    lookupMs?: number;
    cartAt?: number;
    source?: 'LOCAL' | 'API';
}

export class FastBarcodeReceiver {
    private readonly deps: FastReceiverDeps;
    private readonly seen = new Map<string, FastResult | 'IN_PROGRESS'>();
    private queue: Promise<void> = Promise.resolve();

    constructor(deps: FastReceiverDeps) {
        this.deps = deps;
    }

    /** Remembered result for a request_id (tests / diagnostics). */
    remembered(requestId: string): FastResult | 'IN_PROGRESS' | undefined {
        return this.seen.get(requestId);
    }

    cacheSize(): number {
        return this.seen.size;
    }

    /** One frame from the socket bound to [binding]; settles when it (and every earlier one) is done. */
    handle(raw: any, binding: Binding): Promise<void> {
        const frame = parseBarcodeScanFrame(raw);
        if (!frame) return this.queue;                       // malformed: never touches the Cart
        const marks: Marks = { receivedAt: this.now() };
        this.queue = this.queue.then(() => this.process(frame, binding, marks)).catch(() => undefined);
        return this.queue;
    }

    private now(): number {
        if (this.deps.now) return this.deps.now();
        return typeof performance !== 'undefined' && performance.now ? performance.now() : Date.now();
    }

    private remember(id: string, value: FastResult | 'IN_PROGRESS') {
        if (this.seen.has(id)) this.seen.delete(id);
        this.seen.set(id, value);
        while (this.seen.size > FAST_CACHE_SIZE) {
            this.seen.delete(this.seen.keys().next().value as string);
        }
    }

    private async process(frame: BarcodeScanFrame, binding: Binding, marks: Marks): Promise<void> {
        marks.startedAt = this.now();
        const previous = this.seen.get(frame.request_id);
        if (previous !== undefined) {
            // The SAME result again, never a second Cart change. (IN_PROGRESS cannot
            // be seen here -- frames run one at a time -- but would also never re-apply.)
            if (previous !== 'IN_PROGRESS') this.answer(frame, binding, previous, marks, 'DUPLICATE');
            return;
        }
        this.remember(frame.request_id, 'IN_PROGRESS');

        const before = this.deps.receiverState();
        if (before !== 'READY' || !sameBinding(this.deps.currentBinding(), binding)) {
            this.deps.onRejected?.(before !== 'READY' ? STATE_REASON[before] : 'SALE_NOT_READY');
            return this.finish(frame, binding, 'NOT_READY', marks);
        }

        let lookup: LookupOutcome;
        const lookupStarted = this.now();
        try {
            lookup = await this.deps.lookup(frame.barcode);
        } catch {
            lookup = { found: false, reason: 'PRODUCT_LOOKUP_UNAVAILABLE' };
        }
        marks.lookupMs = this.now() - lookupStarted;
        if (!lookup.found) {
            this.deps.onRejected?.(lookup.reason);
            return this.finish(frame, binding, lookup.reason === 'PRODUCT_NOT_FOUND' ? 'NOT_FOUND' : 'NOT_READY', marks);
        }
        if (lookup.source) marks.source = lookup.source;

        // The page may have moved on while the lookup was in flight (local-only check).
        const after = this.deps.receiverState();
        if (after !== 'READY' || !sameBinding(this.deps.currentBinding(), binding)) {
            this.deps.onRejected?.(after !== 'READY' ? STATE_REASON[after] : 'SALE_NOT_READY');
            return this.finish(frame, binding, 'NOT_READY', marks);
        }

        let mutated: MutateOutcome;
        try {
            mutated = this.deps.mutate(lookup.product);
        } catch {
            mutated = { status: 'AMBIGUOUS' };
        }
        if (mutated.status === 'NOT_APPLIED') {
            this.deps.onRejected?.('CART_MUTATION_FAILED');
            return this.finish(frame, binding, 'NOT_READY', marks);
        }
        if (mutated.status === 'AMBIGUOUS') {
            this.deps.onCheck?.();
            return this.finish(frame, binding, 'CHECK', marks);
        }
        marks.cartAt = this.now();
        this.deps.onApplied?.(mutated.product);
        return this.finish(frame, binding, 'OK', marks);
    }

    private finish(frame: BarcodeScanFrame, binding: Binding, result: FastResult, marks: Marks) {
        this.remember(frame.request_id, result);
        this.answer(frame, binding, result, marks, result);
    }

    /** ONE send, no retry: an uncertain barcode is never re-sent or re-applied. */
    private answer(frame: BarcodeScanFrame, binding: Binding, result: FastResult, marks: Marks,
        outcome: FastTiming['outcome']) {
        const decidedAt = this.now();
        let sent = false;
        try {
            sent = this.deps.sendAck(binding, frame.request_id, result);
        } catch {
            sent = false;
        }
        this.report(marks, outcome, decidedAt, sent);
    }

    private report(marks: Marks, outcome: FastTiming['outcome'], decidedAt: number, ackSent: boolean) {
        if (!this.deps.onTiming) return;
        try {
            const end = this.now();
            const timing: FastTiming = {
                path: 'FAST',
                outcome,
                queuedMs: Math.round((marks.startedAt ?? end) - marks.receivedAt),
                ackMs: Math.round(end - decidedAt),
                totalMs: Math.round(end - marks.receivedAt),
                ackSent,
            };
            if (marks.source) timing.source = marks.source;
            if (marks.lookupMs !== undefined) timing.lookupMs = Math.round(marks.lookupMs);
            if (marks.cartAt !== undefined) timing.cartMs = Math.round(marks.cartAt - marks.receivedAt);
            this.deps.onTiming(timing);
        } catch {
            // measurement never changes a scan's outcome
        }
    }
}
